import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ChevronRight from 'lucide-react/dist/esm/icons/chevron-right';
import Home from 'lucide-react/dist/esm/icons/home';

interface BreadcrumbItem {
  label: string;
  path: string;
  icon?: React.ReactNode;
}

interface BreadcrumbProps {
  className?: string;
}

export function Breadcrumb({ className = '' }: BreadcrumbProps) {
  const location = useLocation();
  const navigate = useNavigate();

  // Define the breadcrumb mapping for different routes
  const getBreadcrumbs = (): BreadcrumbItem[] => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const breadcrumbs: BreadcrumbItem[] = [
      { label: 'Dashboard', path: '/dashboard', icon: <Home size={16} /> }
    ];

    if (pathSegments.length === 0) {
      return breadcrumbs;
    }

    // Map route segments to readable labels
    const segmentLabels: Record<string, string> = {
      // Main sections
      'digital': 'Digital Estate',
      'legal': 'Legal Estate',
      'financial': 'Financial',
      'health': 'Health',
      'wellbeing': 'Wellbeing',
      'pets': 'Pets',
      'ndis': 'NDIS',
      'legacy': 'Legacy',
      'files': 'My Files',
      'travel': 'Travel',
      'calendar': 'Calendar',
      'settings': 'Settings',
      'emergency': 'Emergency Management',
      
      // Financial sub-sections
      'bank-accounts': 'Bank Accounts',
      'credit-cards': 'Credit Cards',
      'investments': 'Investments',
      'retirement': 'Retirement',
      'wallet': 'Digital Wallet',
      'assets': 'Financial Assets',
      
      // Health sub-sections
      'appointments': 'Appointments',
      'emergency-contacts': 'Emergency Contacts',
      'health-providers': 'Health Providers',
      'immunizations': 'Immunizations',
      'medical-records': 'Medical Records',
      'medications': 'Medications',
      'records': 'Medical Records',
      
      // Wellbeing sub-sections
      'counselling': 'Counselling',
      'support-groups': 'Support Groups',
      'resources': 'Resources',
      'wellness-planner': 'Wellness Planner',
      'wellbeing-settings': 'Wellbeing Settings',
      
      // Pets sub-sections
      'pet-profiles': 'Pet Profiles',
      'care-schedule': 'Care Schedule',
      'medical-history': 'Medical History',
      'diet-nutrition': 'Diet & Nutrition',
      'vaccination-records': 'Vaccination Records',
      'veterinary-appointments': 'Veterinary Appointments',
      'list': 'Pet List',
      'care': 'Care Schedule',
      'expenses': 'Pet Expenses',
      
      // NDIS sub-sections
      'participants': 'Participants',
      'service-agreements': 'Service Agreements',
      'plan-details': 'Plan Details',
      'service-providers': 'Service Providers',
      'support-coordination': 'Support Coordination',
      'budget-tracker': 'Budget Tracker',
      'quality-assurance': 'Quality Assurance',
      'reports': 'Reports',
      'plan': 'Plan Details',
      'agreements': 'Service Agreements',
      'coordination': 'Support Coordination',
      'budget': 'Budget Tracker',
      'quality': 'Quality Assurance',
      
      // Legacy sub-sections
      'journal': 'Journal',
      'letters': 'Letters',
      'videos': 'Videos',
      'notes': 'Notes',
      'memory-book': 'Memory Book',
      'memorybook': 'Memory Book',
      
      // Travel sub-sections
      'trips': 'Trips',
      'passports': 'Passports',
      'tickets': 'Tickets',
      'itineraries': 'Itineraries',
      'accommodations': 'Accommodations',
      'transportation': 'Transportation',
      'travel-documents': 'Travel Documents',
      'travel-settings': 'Travel Settings',
      'documents': 'Travel Documents',
      
      // Legal sub-sections
      'wills': 'Wills & Trusts',
      'poa': 'Power of Attorney',
      'directives': 'Advance Care Directives',
      'insurance': 'Insurance Policies',
      'checklist': 'Legal Checklist',
      'trust-builder': 'Trust Builder',
      'wills-trusts': 'Wills & Trusts',
      'power-attorney': 'Power of Attorney',
      'advance-care': 'Advance Care',
      'insurance-policies': 'Insurance Policies',
      'legal-contacts': 'Legal Contacts',
      'legal-checklist': 'Legal Checklist',
      
      // Digital sub-sections
      'security': 'Password Vault',
      'subscriptions': 'Subscriptions',
      'devices': 'Devices',
      
      // Calendar sub-sections
      'sharing': 'Calendar Sharing',
      'reminders': 'Reminders',
      
      // Emergency sub-sections
      'profile': 'Profile',
      'hazards': 'Hazard Plans',
      'packs': 'Emergency Packs',
      'emergency-card': 'Emergency Card',
      'card': 'Emergency Card',
      'drills': 'Drills',
      'quickstart': 'Quick Start'
    };

    let currentPath = '';
    for (let i = 0; i < pathSegments.length; i++) {
      const segment = pathSegments[i];
      currentPath += `/${segment}`;
      
      // Skip the first segment if it's 'dashboard' since we already have Dashboard as the first breadcrumb
      if (i === 0 && segment === 'dashboard') {
        continue;
      }
      
      let label =
        segmentLabels[segment] ||
        segment.charAt(0).toUpperCase() + segment.slice(1);
      if (segment === 'contacts') {
        const parent = pathSegments[i - 1];
        if (parent === 'emergency') label = 'Contact Tree';
        else if (parent === 'legal') label = 'Legal Contacts';
      }
      breadcrumbs.push({
        label,
        path: currentPath
      });
    }

    return breadcrumbs;
  };

  const breadcrumbs = getBreadcrumbs();

  const handleBreadcrumbClick = (path: string) => {
    navigate(path);
  };

  if (breadcrumbs.length <= 1) {
    return null; // Don't show breadcrumb if we're just on the dashboard
  }

  return (
    <nav className={`flex items-center space-x-2 text-sm text-gray-600 ${className}`}>
      {breadcrumbs.map((item, index) => (
        <React.Fragment key={item.path}>
          <button
            onClick={() => handleBreadcrumbClick(item.path)}
            className={`flex items-center space-x-1 hover:text-idecide-primary transition-colors ${
              index === breadcrumbs.length - 1 
                ? 'text-idecide-primary font-medium' 
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {item.icon && <span className="flex-shrink-0">{item.icon}</span>}
            <span className="truncate">{item.label}</span>
          </button>
          {index < breadcrumbs.length - 1 && (
            <ChevronRight size={16} className="text-gray-400 flex-shrink-0" />
          )}
        </React.Fragment>
      ))}
    </nav>
  );
} 